/**
 * @fileoverview Document Category Definitions
 *
 * Purpose: Defines 19 document categories with predefined subcategories for
 * LLM classification in insolvency and restructuring investigations.
 *
 * Used by:
 * - convex/jobs/gatherInformation/classification/ (LLM classification)
 * - convex/jobs/gatherInformation/documents/ (queries, mutations)
 * - Frontend components (category labels, filters, icons)
 */

// ---------------------------------------------------------------------------
// Category Definitions
// ---------------------------------------------------------------------------

/**
 * The 19 document categories + "other" fallback.
 * These are the only valid values for the `category` field.
 */
export const DOCUMENT_CATEGORIES = {
  CORPORATE_DOCUMENTS: "corporate_documents",
  DIRECTOR_DISCLOSURES: "director_disclosures",
  ASSETS: "assets",
  SECURITY_INTERESTS: "security_interests",
  BANK_STATEMENTS_TRANSACTIONS: "bank_statements_transactions",
  FINANCIAL_LEDGERS: "financial_ledgers",
  GENERAL_LEDGERS: "general_ledgers",
  FORECASTS_BUDGET: "forecasts_budget",
  GENERAL_LIABILITIES: "general_liabilities",
  EMPLOYMENT: "employment",
  TAX_AND_SRO: "tax_and_sro",
  PROOF_OF_DEBT: "proof_of_debt",
  LOANS: "loans",
  LEGAL_DISPUTES: "legal_disputes",
  INSURANCE: "insurance",
  CONTRACTS: "contracts",
  INVOICES: "invoices",
  FIRM_GENERATED: "firm_generated",
  OTHER: "other",
} as const;

export type DocumentCategoryKey =
  (typeof DOCUMENT_CATEGORIES)[keyof typeof DOCUMENT_CATEGORIES];

/** All valid category strings as an array — used for validation. */
export const VALID_CATEGORIES: string[] = Object.values(DOCUMENT_CATEGORIES);

// ---------------------------------------------------------------------------
// Subcategory Definitions
// ---------------------------------------------------------------------------

/**
 * Predefined subcategories for each category.
 * The LLM selects one of these during classification.
 */
export const DOCUMENT_SUBCATEGORIES: Record<string, string[]> = {
  corporate_documents: [
    "Company Search",
    "Business name search",
    "Director Searches",
    "AML / Compliance Searches",
    "Constitution",
    "Share registry",
    "Pre-appointment meeting minutes",
    "Circular resolutions",
  ],
  director_disclosures: ["ROCAPs", "Questionnaires"],
  assets: [
    "Inventory reports",
    "Asset register",
    "Land title search",
    "Crypto searches",
    "Aged receivables",
    "Asset sale agreements",
    "Vehicle / Vessel Searches",
  ],
  security_interests: ["PPSR Search", "Security Agreements"],
  bank_statements_transactions: [
    "Bank Statements",
    "Bank Transactions",
    "Cashbook extract",
    "Cashflow statement",
  ],
  financial_ledgers: [
    "Profit and loss statements",
    "Balance Sheet",
    "Trial Balance",
  ],
  general_ledgers: ["General Ledger Detail", "GL Transaction Export"],
  forecasts_budget: ["Forecasts", "Budgets"],
  general_liabilities: [
    "Aged payables",
    "Accounts Payable Ledgers",
    "Creditor/Supplier registers",
  ],
  contracts: [
    "Trade Agreements",
    "Supply Agreements",
    "Service Agreements",
    "Leases",
    "Other Contracts",
  ],
  invoices: [
    "Supplier Invoices",
    "Utility Invoices",
    "Professional Fee Invoices",
    "Other Invoices",
  ],
  employment: ["Payroll summaries", "STP", "Employment Agreements"],
  tax_and_sro: [
    "Tax returns",
    "Tax lodgements",
    "Payment plans",
    "ATO communication",
    "ATO Statements",
    "SRO Statements",
    "SRO Lodgements",
  ],
  proof_of_debt: ["Proof of debt forms", "Proof of debt supporting documents"],
  loans: ["Loan accounts", "Loan agreements"],
  legal_disputes: [
    "Letters of demand",
    "Statutory demands",
    "Legal correspondence",
    "Settlement agreements",
  ],
  insurance: ["Insurance policies", "Claims"],
  firm_generated: [
    "ASIC Forms & Statutory Reports",
    "ATO Notifications & Forms",
    "DIRRI",
    "Notification to Creditors",
    "Report to Creditors",
    "Meeting Notes & Minutes",
    "Remuneration Reports",
    "Statutory Declarations",
    "Proof of Publication",
    "Correspondence to ASIC",
    "Other Firm Documents",
  ],
  other: ["Documents not categorised"],
};

/** Flat set of all valid subcategory strings for fast lookup. */
const ALL_VALID_SUBCATEGORIES = new Set(
  Object.values(DOCUMENT_SUBCATEGORIES).flat(),
);

// ---------------------------------------------------------------------------
// Category Config (label, icon name, colour)
// ---------------------------------------------------------------------------

export interface CategoryConfigEntry {
  label: string;
  /** Lucide icon name for the frontend */
  icon: string;
  /** Tailwind colour class stem (e.g. "blue" → "bg-blue-100 text-blue-700") */
  colour: string;
}

export const CATEGORY_CONFIG: Record<string, CategoryConfigEntry> = {
  corporate_documents: {
    label: "Corporate Documents",
    icon: "Building2",
    colour: "slate",
  },
  director_disclosures: {
    label: "Director Disclosures",
    icon: "UserCheck",
    colour: "indigo",
  },
  assets: { label: "Assets", icon: "Truck", colour: "teal" },
  security_interests: {
    label: "Security Interests",
    icon: "Shield",
    colour: "purple",
  },
  bank_statements_transactions: {
    label: "Bank Statements & Transactions",
    icon: "Landmark",
    colour: "cyan",
  },
  financial_ledgers: {
    label: "Financial Ledgers",
    icon: "BarChart3",
    colour: "blue",
  },
  general_ledgers: {
    label: "General Ledgers",
    icon: "BookOpen",
    colour: "sky",
  },
  forecasts_budget: {
    label: "Forecasts & Budget",
    icon: "ChartNoAxesCombined",
    colour: "lime",
  },
  general_liabilities: {
    label: "General Liabilities",
    icon: "Users",
    colour: "orange",
  },
  employment: { label: "Employment", icon: "Users", colour: "violet" },
  tax_and_sro: { label: "Tax and SRO", icon: "Receipt", colour: "amber" },
  proof_of_debt: {
    label: "Proof of Debt",
    icon: "ClipboardCheck",
    colour: "rose",
  },
  loans: { label: "Loans", icon: "HandCoins", colour: "emerald" },
  legal_disputes: { label: "Legal Disputes", icon: "Scale", colour: "red" },
  insurance: { label: "Insurance", icon: "ShieldCheck", colour: "green" },
  contracts: { label: "Contracts", icon: "FolderClosed", colour: "pink" },
  invoices: { label: "Invoices", icon: "DollarSign", colour: "yellow" },
  firm_generated: {
    label: "Firm Generated",
    icon: "BriefcaseBusiness",
    colour: "fuchsia",
  },
  other: { label: "Other", icon: "HelpCircle", colour: "neutral" },
};

// ---------------------------------------------------------------------------
// Derived Maps
// ---------------------------------------------------------------------------

/** Simple key → label map derived from CATEGORY_CONFIG. */
export const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => [key, cfg.label]),
);

// ---------------------------------------------------------------------------
// Validation Helpers
// ---------------------------------------------------------------------------

/** Returns true if the given string is one of the 19 categories (or "other"). */
export function isValidCategory(category: string): boolean {
  return VALID_CATEGORIES.includes(category);
}

/** Returns true if the given subcategory is valid for the given category. */
export function isValidSubcategory(
  category: string,
  subcategory: string,
): boolean {
  const validSubs = DOCUMENT_SUBCATEGORIES[category];
  if (!validSubs) return false;
  return validSubs.includes(subcategory);
}

/** Returns true if the given string is any valid subcategory (regardless of category). */
export function isAnyValidSubcategory(subcategory: string): boolean {
  return ALL_VALID_SUBCATEGORIES.has(subcategory);
}

// ---------------------------------------------------------------------------
// Legacy Compatibility
// ---------------------------------------------------------------------------

/**
 * Maps old 12-category keys to new 15-category keys.
 * Used for backward compatibility with existing classified documents.
 */
export const LEGACY_CATEGORY_MAP: Record<string, string> = {
  financial: "financial_ledgers",
  banking: "bank_statements_transactions",
  tax_and_compliance: "tax_and_sro",
  creditors: "general_liabilities",
  employment: "employment",
  corporate: "corporate_documents",
  legal: "legal_disputes",
  insolvency: "director_disclosures",
  correspondence: "other",
  property: "assets",
  governance: "corporate_documents",
  operational: "assets",
  uncategorised: "other",
};

/**
 * @deprecated Legacy helper — maps old subcategories to the new category system.
 * Kept for backward compatibility with Phase 3 agent orchestrator.
 */
const LEGACY_SUBCATEGORY_TO_PARENT: Record<string, string> = {
  // Financial statements & records → "financial_ledgers"
  accounting_file: "financial_ledgers",
  general_ledger: "general_ledgers",
  cash_book: "bank_statements_transactions",
  profit_and_loss: "financial_ledgers",
  balance_sheet: "financial_ledgers",
  cash_flow_statement: "bank_statements_transactions",
  related_party_loan_account: "loans",
  aged_receivables: "assets",
  forecast_budget: "forecasts_budget",
  statutory_accounts: "financial_ledgers",
  fixed_asset_register: "assets",

  // Bank statements & correspondence → "bank_statements_transactions"
  bank_statement: "bank_statements_transactions",
  bank_correspondence: "bank_statements_transactions",

  // Tax & compliance → "tax_and_sro"
  tax_return: "tax_and_sro",
  bas_return: "tax_and_sro",
  ato_correspondence: "tax_and_sro",

  // Creditors → "general_liabilities"
  aged_payables: "general_liabilities",

  // Corporate records → "corporate_documents"
  trust_deed: "corporate_documents",
  statutory_register: "corporate_documents",
  company_search: "corporate_documents",
  business_names_search: "corporate_documents",
  director_search: "corporate_documents",

  // Governance → "corporate_documents"
  board_minutes: "corporate_documents",

  // Legal → various
  loan_agreement: "loans",
  sale_agreement: "assets",
  contract: "legal_disputes",
  insurance_policy: "insurance",
  ppsr_search: "security_interests",

  // Property → "assets"
  vehicle_vessel_search: "assets",
  land_titles_search: "assets",

  // Insolvency → "director_disclosures"
  rocap: "director_disclosures",
  crypto_search: "assets",

  // Correspondence → "other"
  director_correspondence: "other",
  accountant_correspondence: "other",
  lawyer_correspondence: "other",
  other_correspondence: "other",
  email: "other",

  // Operational → "assets"
  cloud_storage_file: "other",

  // Fallback
  other: "other",
};

/**
 * @deprecated Returns the parent category for a given subcategory.
 * Kept for backward compatibility with Phase 3 agent orchestrator.
 */
export function getParentCategory(subCategory: string): string {
  return LEGACY_SUBCATEGORY_TO_PARENT[subCategory] ?? "other";
}
